// src/utils/versionCheck.js
//
// 16 Jul 2026 — new. Solves a real gap the SHA build-marker (buildInfo.js)
// left open: that marker only tells you what build a tab loaded WITH — it
// can't tell you if a NEWER build has since been deployed while the tab
// stayed open. For a single-page app, once index.html loads once, React
// Router handles all navigation client-side — no new page load, no new
// fetch, nothing ever re-checks index.html again for the lifetime of that
// tab. A tab left open across a deploy can run arbitrarily old JS
// indefinitely; a hard refresh is the only thing that currently fixes it.
//
// This polls a small, already-existing CRA build artifact
// (asset-manifest.json — lists the exact current main.[hash].js filename)
// on an interval AND whenever the tab regains focus (the most likely
// moment a stale tab actually matters — someone switching back to a
// browser tab they left open earlier). If the filename differs from what
// this tab is actually running, it dispatches a 'previu:update-available'
// CustomEvent — same convention as the existing 'previu:session-expired'
// event api.js's 401 interceptor already fires — so ToastContext can show
// a persistent, dismissible "refresh to update" notice without this
// module needing to know anything about React or toasts.
//
// Deliberately does NOT auto-reload. Forcing a reload out from under
// someone mid-task (e.g. filling in a lab order form) would be worse than
// the staleness itself — this only ever notifies, the person decides when.

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min — frequent enough to matter, not so
                                          // frequent it's a meaningful load on Vercel/CDN

// Once a new version has been detected there is nothing more to learn — the
// running tab stays on `current` until an actual reload, so every later poll
// or focus check would just re-detect the same staleness. Without this guard
// checkForNewVersion re-dispatches 'previu:update-available' on every 5-minute
// tick and every tab focus, and ToastContext has no dedup of its own, so a
// tab left open past one deploy accumulates a new persistent "Refresh now"
// toast each time — stacking indefinitely instead of showing the notice once.
let notified = false;

function getCurrentBundleFilename() {
  // The currently-running bundle's own <script> tag — reading this directly
  // avoids needing a separately-baked-in "expected" value that could itself
  // drift out of sync with what's actually executing.
  const script = document.querySelector('script[src*="/static/js/main."]');
  if (!script) return null;
  return script.getAttribute('src').split('/').pop();
}

async function checkForNewVersion() {
  if (notified) return; // already told this tab once — wait for it to reload
  const current = getCurrentBundleFilename();
  if (!current) return; // can't determine — fail silent, don't false-positive

  try {
    // Cache-busting query param as belt-and-braces alongside vercel.json's
    // no-cache header on this exact path — two independent reasons this
    // fetch shouldn't return a stale manifest.
    const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const manifest = await res.json();
    const latestMainPath = manifest?.files?.['main.js'];
    if (!latestMainPath) return;
    const latest = latestMainPath.split('/').pop();

    if (latest !== current) {
      console.log('[Previu] new version detected:', { running: current, latest });
      notified = true;
      window.dispatchEvent(new CustomEvent('previu:update-available'));
    }
  } catch (e) {
    // Network hiccup, offline, etc. — not worth surfacing to the user;
    // next interval/focus check will just try again.
    console.warn('[Previu] version check failed (will retry):', e.message);
  }
}

export function startVersionCheck() {
  // Interval check — catches a tab left open and untouched for a long stretch.
  setInterval(checkForNewVersion, POLL_INTERVAL_MS);

  // Focus check — catches the more common case directly: someone switching
  // back to a Previu tab they opened earlier today, which is exactly the
  // scenario that prompted building this.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForNewVersion();
  });

  // Also check once shortly after startup, in case a deploy landed in the
  // few minutes between this tab loading and now.
  setTimeout(checkForNewVersion, POLL_INTERVAL_MS);
}
